import { getSanitizeStyleBreadcrumb, sanitizeLogger } from './sanitizeLogger';
import { parse, Root, AtRule, Rule, Declaration } from 'postcss';
import * as valueParserModule from 'postcss-value-parser';
import { CONFIG_SANITIZE_STYLE } from './config';

const valueParser = (valueParserModule as any).default || valueParserModule;

/**
 * Валидный фрагментный URL `url(#id)` — ссылка на элемент внутри того же документа
 * (в SVG так ссылаются на градиенты/фильтры). Пропускает unicode-буквы/цифры,
 * т.к. id в реальных SVG бывают кириллическими (напр. `#Безымянный_градиент_2`);
 * `\w` такие символы не матчит, поэтому используем unicode-property escapes
 * (конструктор RegExp — литерал с `\p{...}` требует target ES2018+).
 */
const SAFE_FRAGMENT_URL = new RegExp('^#[\\p{L}\\p{N}_:.-]+$', 'u');

/** Общая логика очистки: проход по декларациям в правиле */
const sanitizeRuleDecls = (rule: Rule, styleOrCss: string): void => {
	rule.walkDecls((declaration: Declaration) => {
		const property = declaration.prop;
		const value = String(declaration.value ?? '');

		const { FORBIDDEN_PROPERTIES, FORBIDDEN_VALUES, SAFE_DATA_IMAGE, SAFE_URL } = CONFIG_SANITIZE_STYLE;

		/** Удаляет декларацию если есть запрещенные свойства  */
		if (FORBIDDEN_PROPERTIES.some(regexp => regexp.test(property))) {
			sanitizeLogger(getSanitizeStyleBreadcrumb(property, 'property', styleOrCss));

			declaration.remove();

			return;
		}

		/** Удаляет декларацию если есть запрещенные значения  */
		if (FORBIDDEN_VALUES.some(regexp => regexp.test(value))) {
			sanitizeLogger(getSanitizeStyleBreadcrumb(value, 'value', styleOrCss));

			declaration.remove();

			return;
		}

		/** Проверка url(...) на запрещенные значения */
		let unsafe = false;
		/** Парсинг значение декларации на ноды */
		const parsed = valueParser(value);

		parsed.walk((node: any) => {
			if (node.type === 'function' && node.value.toLowerCase() === 'url') {
				/** Собирает ноды в чистое содержимое из url(...) */
				const inner = (valueParser as any)
					.stringify(node.nodes)
					.trim()
					.replace(/^['"]|['"]$/g, '');

				/** Ищет опасные типы data */
				if (/^data:/i.test(inner)) {
					if (!SAFE_DATA_IMAGE.test(inner)) {
						unsafe = true;

						return false;
					}
					/** Ищет опасные url. Фрагментные ссылки url(#id) на элементы документа безопасны */
				} else if (!SAFE_URL.test(inner) && !SAFE_FRAGMENT_URL.test(inner)) {
					unsafe = true;

					return false;
				}
			}

			/** Продолжить обход */
			return undefined;
		});

		/** Удаляет декларацию, если есть небезопасные значения */
		if (unsafe) {
			sanitizeLogger(getSanitizeStyleBreadcrumb(value, 'url', styleOrCss));

			declaration.remove();

			return;
		}
	});

	/** Если после очистки, селектор оказался пустой, он полностью удаляется */
	if (!rule.nodes || rule.nodes.length === 0) {
		rule.remove();
	}
};

/** Сериализация inline-стилей из правила: "prop: value; prop2: value2" */
const serializeInlineFromRule = (rule: Rule): string | null => {
	const declarations: string[] = [];

	rule.nodes?.forEach(node => {
		if (node.type === 'decl') {
			declarations.push(`${node.prop}: ${node.value}`);
		}
	});

	return declarations.length ? declarations.join('; ') : null;
};

/**
 * Чистит inline style, оставляя только безопасные.
 *
 * - Если содержащие опасные или невалидные конструкции (expression, `@import`, javascript: и т.п.), возвращает `null`.
 * - Для свойств с url(...) проверяет каждую ссылку по SAFE_URL или фрагментному SAFE_FRAGMENT_URL (url(#id)), небезопасные — вырезает.
 * - Если после фильтрации не остаётся ни одного объявления, возвращает `null`.
 *
 * @param style Строка со значением атрибута style (например, "color:red;background:url(...)").
 * @returns Строка с безопасными style или `null`, если ничего не осталось.
 */

export const sanitizeInlineStyle = (style: string): string | null => {
	if (!style?.trim()) {
		return null;
	}

	let root: Root;

	/**
	 * Ошибка при парсинге может быть, если внутри inline style,
	 * есть опасные или невалидные декларации, значения, которые parse не может распарсить.
	 * В таком случае отбрасываем весь атрибут style.
	 */
	try {
		root = parse(`#x{${style}}`);
	} catch (e) {
		sanitizeLogger(getSanitizeStyleBreadcrumb(`${e}`, 'parse', style));

		return null;
	}

	const rule = root.first;
	if (rule?.type !== 'rule') {
		return null;
	}

	sanitizeRuleDecls(rule, style);

	return serializeInlineFromRule(rule);
};

/**
 * Чистит полноценный CSS (содержимое <style>).
 * - удаляет комментарии
 * - оставляет только allowlisted @-правила (по умолчанию: @media, @keyframes)
 * - вырезает декларации с опасными значениями и небезопасными url(...)
 * - удаляет пустые правила
 *
 * @param css Строка со значением содердимого тега `<style>`
 * @returns Строка с безопасными css или `null`, если ничего не осталось.
 */
export const sanitizeStyleBlock = (css: string): string | null => {
	if (!css?.trim()) {
		return '';
	}

	const { ALLOW_AT_RULES, FORBIDDEN_AT_RULES } = CONFIG_SANITIZE_STYLE;

	let root: Root;

	/**
	 * Ошибка при парсинге может быть, если внутри тега <style>,
	 * есть опасные или невалидные декларации, значения, которые parse не может распарсить.
	 * В таком случае отбрасываем весь тег <style>.
	 */
	try {
		root = parse(css);
	} catch (e) {
		sanitizeLogger(getSanitizeStyleBreadcrumb(`${e}`, 'parse', css));

		return null;
	}

	/** Удаляет все комментарии */
	root.walkComments(comment => comment.remove());

	/** Проходится по всем @-правилам */
	root.walkAtRules((at: AtRule) => {
		const name = at.name.toLowerCase();

		/** Удаляет явно опасные */
		if (FORBIDDEN_AT_RULES.includes(name)) {
			sanitizeLogger(getSanitizeStyleBreadcrumb(name, 'rule', css));

			at.remove();
		}

		/** Пропускает только разрешенные */
		if (ALLOW_AT_RULES.includes(name)) {
			return;
		}

		/** Удаляет все остальные неизвестные */
		sanitizeLogger(getSanitizeStyleBreadcrumb(name, 'rule', css));
		at.remove();
	});

	/** Проходится по всем селекторам */
	root.walkRules((rule: Rule) => {
		sanitizeRuleDecls(rule, css);
	});

	return root.toString().trim();
};
